<!-- eslint-disable vuejs-accessibility/no-autofocus -->
<template>
  <v-container class="pa-12">
    <v-toolbar color="white">
      <v-toolbar-title>Risk Matrix Management</v-toolbar-title>
      <template #extension>
        <v-tabs v-model="tab" color="primary" slider-color="primary" align-tabs="title">
          <v-tab
            v-for="item in items"
            :id="getTabId(item)"
            :key="item"
            :text="item"
            :value="item"
            max-height="40"
          />
        </v-tabs>
      </template>
    </v-toolbar>
    <v-tabs-window v-model="tab">
      <v-tabs-window-item v-for="tabItem in items" :key="tabItem" :value="tabItem">
        <v-card flat>
          <div v-if="tabItem === 'Risk Matrix'">
            <v-card class="h-screen">
              <table class="matrix-preview-table">
                <tbody>
                  <tr>
                    <td class="likelihoodAxis">{{ likelihoodAxis[0] }}</td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell highCell"></td>
                    <td class="risk-matrix-cell extremeCell"></td>
                    <td class="risk-matrix-cell extremeCell"></td>
                  </tr>
                  <tr>
                    <td class="likelihoodAxis">{{ likelihoodAxis[1] }}</td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell highCell"></td>
                    <td class="risk-matrix-cell extremeCell"></td>
                    <td class="risk-matrix-cell extremeCell"></td>
                  </tr>
                  <tr>
                    <td class="likelihoodAxis">{{ likelihoodAxis[2] }}</td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell highCell"></td>
                    <td class="risk-matrix-cell extremeCell"></td>
                  </tr>
                  <tr>
                    <td class="likelihoodAxis">{{ likelihoodAxis[3] }}</td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell highCell"></td>
                  </tr>
                  <tr>
                    <td class="likelihoodAxis">{{ likelihoodAxis[4] }}</td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell lowCell"></td>
                    <td class="risk-matrix-cell moderateCell"></td>
                    <td class="risk-matrix-cell highCell"></td>
                  </tr>
                  <tr>
                    <td class="consequenceAxis"></td>
                    <td class="consequenceAxis">{{ consequenceAxis[0] }}</td>
                    <td class="consequenceAxis">{{ consequenceAxis[1] }}</td>
                    <td class="consequenceAxis">{{ consequenceAxis[2] }}</td>
                    <td class="consequenceAxis">{{ consequenceAxis[3] }}</td>
                    <td class="consequenceAxis">{{ consequenceAxis[4] }}</td>
                  </tr>
                </tbody>
              </table>
              <div class="riskRatingLegend">
                <div
                  v-for="(item, index) in computedRiskRating"
                  :key="item.sid"
                  :class="{
                    lowEditor: index === 0,
                    mediumEditor: index === 1,
                    highEditor: index === 2,
                    extremeEditor: index === 3,
                    riskRatingLegendEditor: index >= 0
                  }"
                >
                  <div @click="openRiskMatrixEditDialog(index)">
                    <span :id="getEditFieldLabelId('matrix-risk-rating-definition-title', index)">
                      {{ item.label }}
                    </span>
                    <div class="risk-rating-edit">
                      <v-icon
                        :id="getEditIconId('matrix-risk-rating-definition-icon', index)"
                        size="16"
                      >
                        mdi-pencil
                      </v-icon>
                    </div>
                  </div>
                  <v-confirm-edit
                    v-if="riskMatrixEditDialogIndex === index"
                    v-model="item.label"
                    color="primary"
                    @save="save"
                    @cancel="closeEditDialog"
                  >
                    <template #default="{ model: proxyModel, actions }">
                      <v-card v-click-outside="closeEditDialog" class="confirm-edit-overlay">
                        <template #text>
                          <v-text-field
                            :id="getEditFieldId('matrix-risk-rating-definition-title', index)"
                            v-model="proxyModel.value"
                            label="Edit"
                            single-line
                            color="primary"
                            variant="underlined"
                            autofocus
                          />
                        </template>

                        <template #actions>
                          <v-spacer></v-spacer>
                          <component :is="actions" class="risk-matrix-edit-action-btn"></component>
                        </template>
                      </v-card>
                    </template>
                  </v-confirm-edit>
                </div>
              </div>
            </v-card>
          </div>
          <div v-if="tabItem === 'Likelihood'">
            <v-card class="h-screen">
              <v-data-table
                :headers="headers"
                :items="computedLikelihood"
                hide-default-footer
                item-value="sid"
                density="comfortable"
                class="matrix-table h-screen"
              >
                <template #item="{ item, index }">
                  <tr>
                    <td
                      :id="getEditFieldLabelId('matrix-likelihood-definition-title', index)"
                      class="matrix-cell-left"
                      @click="openLikelihoodTitleEditDialog(index)"
                    >
                      {{ item.label }}
                      <div class="matrix-edit">
                        <v-icon
                          :id="getEditIconId('matrix-likelihood-definition-title-icon', index)"
                          size="16"
                        >
                          mdi-pencil
                        </v-icon>
                      </div>
                      <v-confirm-edit
                        v-if="likelihoodEditTitleDialogIndex === index"
                        v-model="item.label"
                        color="primary"
                        @save="save"
                        @cancel="closeEditDialog"
                      >
                        <template #default="{ model: proxyModel, actions }">
                          <v-card v-click-outside="closeEditDialog" class="confirm-edit-overlay">
                            <template #text>
                              <v-text-field
                                :id="getEditFieldId('matrix-likelihood-definition-title', index)"
                                v-model="proxyModel.value"
                                label="Edit"
                                single-line
                                color="primary"
                                variant="underlined"
                                autofocus
                              />
                            </template>

                            <template #actions>
                              <v-spacer></v-spacer>
                              <component
                                :is="actions"
                                class="likelihood-title-edit-action-btn"
                              ></component>
                            </template>
                          </v-card>
                        </template>
                      </v-confirm-edit>
                    </td>
                    <td
                      :id="getEditFieldLabelId('matrix-likelihood-definition-description', index)"
                      class="matrix-cell-right"
                      @click="openLikelihoodDescEditDialog(index)"
                    >
                      {{ item.description }}
                      <div class="matrix-edit">
                        <v-icon
                          :id="
                            getEditIconId('matrix-likelihood-definition-description-icon', index)
                          "
                          size="16"
                        >
                          mdi-pencil
                        </v-icon>
                      </div>
                      <v-confirm-edit
                        v-if="likelihoodEditDescDialogIndex === index"
                        v-model="item.description"
                        color="primary"
                        @save="save"
                        @cancel="closeEditDialog"
                      >
                        <template #default="{ model: proxyModel, actions }">
                          <v-card v-click-outside="closeEditDialog" class="confirm-edit-overlay">
                            <template #text>
                              <v-textarea
                                :id="
                                  getEditFieldId('matrix-likelihood-definition-description', index)
                                "
                                v-model="proxyModel.value"
                                label="Edit"
                                color="primary"
                                variant="underlined"
                                autofocus
                                width="400px"
                              />
                            </template>

                            <template #actions>
                              <v-spacer></v-spacer>
                              <component
                                :is="actions"
                                class="likelihood-description-edit-action-btn"
                              ></component>
                            </template>
                          </v-card>
                        </template>
                      </v-confirm-edit>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </div>
          <div v-if="tabItem === 'Consequence'">
            <v-card class="h-screen">
              <v-data-table
                :headers="headers"
                :items="computedConsequence"
                item-value="sid"
                hide-default-footer
                class="matrix-table h-screen"
              >
                <template #item="{ item, index }">
                  <tr>
                    <td
                      :id="getEditFieldLabelId('matrix-consequence-definition-title', index)"
                      class="matrix-cell-left"
                      @click="openConsequenceTitleEditDialog(index)"
                    >
                      {{ item.label }}
                      <div class="matrix-edit">
                        <v-icon
                          :id="getEditIconId('matrix-consequence-definition-title-icon', index)"
                          size="16"
                        >
                          mdi-pencil
                        </v-icon>
                      </div>
                      <v-confirm-edit
                        v-if="consequenceEditTitleDialogIndex === index"
                        v-model="item.label"
                        color="primary"
                        @save="save"
                        @cancel="closeEditDialog"
                      >
                        <template #default="{ model: proxyModel, actions }">
                          <v-card v-click-outside="closeEditDialog" class="confirm-edit-overlay">
                            <template #text>
                              <v-text-field
                                :id="getEditFieldId('matrix-consequence-definition-title', index)"
                                v-model="proxyModel.value"
                                label="Edit"
                                single-line
                                color="primary"
                                variant="underlined"
                                autofocus
                              />
                            </template>

                            <template #actions>
                              <v-spacer></v-spacer>
                              <component
                                :is="actions"
                                class="consequence-title-edit-action-btn"
                              ></component>
                            </template>
                          </v-card>
                        </template>
                      </v-confirm-edit>
                    </td>
                    <td
                      :id="getEditFieldLabelId('matrix-consequence-definition-description', index)"
                      class="matrix-cell-right"
                      @click="openConsequenceDescEditDialog(index)"
                    >
                      {{ item.description }}
                      <div class="matrix-edit">
                        <v-icon
                          :id="
                            getEditIconId('matrix-consequence-definition-description-icon', index)
                          "
                          size="16"
                        >
                          mdi-pencil
                        </v-icon>
                      </div>
                      <v-confirm-edit
                        v-if="consequenceEditDescDialogIndex === index"
                        v-model="item.description"
                        color="primary"
                        @save="save"
                        @cancel="closeEditDialog"
                      >
                        <template #default="{ model: proxyModel, actions }">
                          <v-card v-click-outside="closeEditDialog" class="confirm-edit-overlay">
                            <template #text>
                              <v-textarea
                                :id="
                                  getEditFieldId('matrix-consequence-definition-description', index)
                                "
                                v-model="proxyModel.value"
                                label="Edit"
                                color="primary"
                                variant="underlined"
                                autofocus
                                width="400px"
                              />
                            </template>

                            <template #actions>
                              <v-spacer></v-spacer>
                              <component
                                :is="actions"
                                class="consequence-description-edit-action-btn"
                              ></component>
                            </template>
                          </v-card>
                        </template>
                      </v-confirm-edit>
                    </td>
                  </tr>
                </template>
              </v-data-table>
            </v-card>
          </div>
        </v-card>
      </v-tabs-window-item>
    </v-tabs-window>
    <cs-form-dialog
      id="dlg-save-generic"
      v-model="groupSaveGenericDialogDisplayed"
      heading="Risk Matrix Management"
      body-id="group-import-modal--generic-dialog"
      :primary-action="{ label: 'OK', eventName: 'primary-click' }"
      @primary-click="groupSaveGenericDialogDisplayed = false"
    >
      <template #cs-form-dialog-content>
        <div v-safe-html="groupSaveGenericDialogMessage"></div>
      </template>
    </cs-form-dialog>
  </v-container>
</template>

<script>
import * as types from '@/store/mutationTypes';
import { CSBase } from '@complispace/cs-design-system';
import { mapState } from 'vuex';
import defaultRiskMatrix from '../lib/const/riskMatrixTemplate';
import { populateErrorMessage } from '../lib/errorMessages';

export default {
  extends: CSBase,
  data() {
    return {
      tab: null,
      items: ['Risk Matrix', 'Likelihood', 'Consequence'],
      headers: [
        {
          title: 'Title',
          align: 'start',
          sortable: false,
          value: 'label'
        },
        { title: 'Definition', value: 'description', sortable: false }
      ],
      groupSaveGenericDialogDisplayed: false,
      groupSaveGenericDialogMessage: '',
      groupSaveGenericDialogSuccessMessage: 'Risk matrix has been updated.',
      riskMatrixEditDialogIndex: null,
      likelihoodEditTitleDialogIndex: null,
      likelihoodEditDescDialogIndex: null,
      consequenceEditTitleDialogIndex: null,
      consequenceEditDescDialogIndex: null
    };
  },
  computed: {
    ...mapState({
      clientSiteSettingsButtonDiplayed: (state) => state.common.clientSiteSettingsButtonDiplayed,
      publicSchoolFeatureSetEnabled: (state) => state.common.publicSchoolFeatureSetEnabled
    }),
    computedLikelihood: {
      get() {
        return this.convertConsequenceOrLikelihood(
          this.$store.state.common.clientSettingRiskMatrix.likelihood ??
            defaultRiskMatrix.likelihood
        );
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_RISK_MATRIX_LIKELIHOOD, value);
      }
    },
    computedConsequence: {
      get() {
        return this.convertConsequenceOrLikelihood(
          this.$store.state.common.clientSettingRiskMatrix.consequence ??
            defaultRiskMatrix.consequence
        );
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_RISK_MATRIX_CONSEQUENCE, value);
      }
    },
    computedRiskRating: {
      get() {
        return this.convertRiskRating(
          this.$store.state.common.clientSettingRiskMatrix.riskRating ??
            defaultRiskMatrix.riskRating
        );
      },
      set(value) {
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX_RISK_RATING, value);
      }
    },
    likelihoodAxis() {
      return this.computedLikelihood.map((item) => item.label).reverse();
    },
    consequenceAxis() {
      return this.computedConsequence.map((item) => item.label);
    }
  },
  async mounted() {
    const { axiosInstance, eventPluginCommonAdapter, stbApiAdapter } = this.$app;
    const isVendorAuthScheme =
      !!this.$route.query['vendor-sid'] && !!this.$route.query['oauth-signature'];
    if (!isVendorAuthScheme) {
      await this.$store.dispatch('authorisation/validateAndSetUserTokenOrLogout', {
        query: this.$route.query,
        axiosInstance
      });
    }
    await this.$store.dispatch('common/initClientConfig', {
      queryParam: isVendorAuthScheme ? this.$route.query : {},
      eventPluginCommonAdapter,
      stbApiAdapter
    });
    if (!this.clientSiteSettingsButtonDiplayed || this.publicSchoolFeatureSetEnabled) {
      this.$router.push('/excursions');
    }
    this.updateRiskMatrix();
  },
  methods: {
    getTabId(description) {
      return `client-admin-risk-matrix-management--tab-item--label-${description.replace(
        / /g,
        '-'
      )}`;
    },
    getEditFieldId(description, index) {
      return `client-admin-risk-matrix-management--risk-matrix--text-input-${description}_${index}`;
    },
    getEditFieldLabelId(description, index) {
      return `client-admin-risk-matrix-management--risk-matrix--text-input-label-${description}_${index}`;
    },
    getEditIconId(description, index) {
      return `client-admin-risk-matrix-management--risk-matrix--edit-icon-${description}_${index}`;
    },
    convertConsequenceOrLikelihood(value) {
      return Object.keys(value)
        .map((item) => ({
          sid: item,
          label: value[item].label,
          description: value[item].description,
          orderIndex: value[item].orderIndex
        }))
        .sort((a, b) => a.orderIndex - b.orderIndex);
    },
    convertRiskRating(value) {
      return Object.keys(value)
        .map((item) => ({
          sid: item,
          label: value[item].label,
          orderIndex: value[item].orderIndex
        }))
        .sort((a, b) => a.orderIndex - b.orderIndex);
    },
    openRiskMatrixEditDialog(index) {
      this.riskMatrixEditDialogIndex = index;
    },
    openLikelihoodTitleEditDialog(index) {
      this.likelihoodEditTitleDialogIndex = index;
    },
    openLikelihoodDescEditDialog(index) {
      this.likelihoodEditDescDialogIndex = index;
    },
    openConsequenceTitleEditDialog(index) {
      this.consequenceEditTitleDialogIndex = index;
    },
    openConsequenceDescEditDialog(index) {
      this.consequenceEditDescDialogIndex = index;
    },
    async save() {
      const updatedRiskRating = this.computedRiskRating.reduce((prev, current) => {
        return { ...prev, [current.sid]: { label: current.label, orderIndex: current.orderIndex } };
      }, {});
      const updatedConsequence = this.computedConsequence.reduce((prev, current) => {
        return {
          ...prev,
          [current.sid]: {
            label: current.label,
            description: current.description,
            orderIndex: current.orderIndex
          }
        };
      }, {});
      const updatedLikelihood = this.computedLikelihood.reduce((prev, current) => {
        return {
          ...prev,
          [current.sid]: {
            label: current.label,
            description: current.description,
            orderIndex: current.orderIndex
          }
        };
      }, {});

      const argHash = {
        payload: {
          riskRating: updatedRiskRating,
          likelihood: updatedLikelihood,
          consequence: updatedConsequence
        },
        store: this.$store
      };
      const cbResponse = await this.$app.stbApiAdapter.putClientRiskMatrix(argHash);
      if (cbResponse.error) {
        let dialogMessage = 'Risk matrix cannot be updated.';
        if (cbResponse.refinedMessage) {
          this.$forceUpdate();
          dialogMessage = `${dialogMessage} ${cbResponse.refinedMessage}.`;
        }

        this.groupSaveGenericDialogMessage = populateErrorMessage({
          message: dialogMessage,
          store: this.$store
        });
        this.groupSaveGenericDialogDisplayed = true;
      } else {
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX, {
          likelihood: updatedLikelihood,
          consequence: updatedConsequence,
          riskRating: updatedRiskRating
        });
      }
      this.closeEditDialog();
    },
    closeEditDialog() {
      this.riskMatrixEditDialogIndex = null;
      this.likelihoodEditTitleDialogIndex = null;
      this.likelihoodEditDescDialogIndex = null;
      this.consequenceEditTitleDialogIndex = null;
      this.consequenceEditDescDialogIndex = null;
    },
    async updateRiskMatrix() {
      const requestParam = {
        'vendor-sid': this.$store.state.common.apiParam['vendor-sid'],
        'oauth-signature': this.$store.state.common.apiParam['oauth-signature']
      };
      const getClientsArgHash = {
        sid: this.$store.state.common.userClientSid,
        requestParam,
        store: this.$store
      };
      const cbResponse = await this.$app.stbApiAdapter.getClientRiskMatrix(getClientsArgHash);

      if (!cbResponse.error) {
        const {
          data: { likelihood, riskRating, consequence }
        } = cbResponse;
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX, {
          likelihood,
          riskRating,
          consequence
        });
      } else {
        this.$store.commit(types.COMMON_SET_CLIENT_SETTING_RISK_MATRIX, {
          likelihood: defaultRiskMatrix.likelihood,
          consequence: defaultRiskMatrix.consequence,
          riskRating: defaultRiskMatrix.riskRating
        });
      }
    }
  }
};
</script>
<style scoped>
.matrix-cell {
  position: relative !important;
  height: 100%;
  width: 100%;
  min-width: 250px;
  display: flex;
  align-items: center;
}
.matrix-cell-left,
.matrix-cell-right {
  position: relative !important;
}
.matrix-cell-left {
  max-width: 250px;
}
.matrix-cell-left:hover .matrix-edit,
.matrix-cell-right:hover .matrix-edit,
.matrix-cell:hover .matrix-edit,
.riskRatingLegendEditor:hover .risk-rating-edit {
  display: block;
}
.matrix-edit {
  position: absolute;
  display: none;
  top: 25%;
  right: 0px;
}
.matrix-table {
  padding-left: 50px;
  padding-right: 50px;
}
.matrix-preview-table {
  margin: 30px auto;
  padding-right: 12%;
  padding-left: 3%;
}
.likelihoodAxis {
  max-width: 200px;
  height: 60px;
}
.consequenceAxis {
  width: 200px;
  height: 90px;
}
.risk-matrix-cell {
  width: 200px;
  height: 60px;
}
.lowCell {
  background-color: green;
}
.moderateCell {
  background-color: #ffff00;
}
.highCell {
  background-color: orange;
}
.extremeCell {
  background-color: red;
}
.riskRatingLegend {
  display: flex;
  width: 100%;
  justify-content: end;
  padding-left: 20px;
}
.riskRatingLegendEditor {
  width: 250px;
  position: relative;
}
.risk-rating-edit {
  position: absolute;
  display: none;
  top: -10%;
  right: 0px;
  padding-right: 20px;
}
.confirm-edit-overlay {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 10;
}
:deep(.matrix-table .v-data-table-header__content) {
  font-weight: bold;
}
</style>
